import React, { ReactElement, useEffect } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { Auth } from '@aws-amplify/auth';

import { useTranslate } from 'hooks/translate';

import { parksApi } from 'api/bestpark/parks';
import { reservationsApi } from 'api/bestpark/reservations';
import PageLoader from 'components/page-loader/page-loader';
import { PROFILE_PATH } from 'constants/paths';
import { useAuth } from 'contexts/auth-context';
import { eraseCookie } from 'helpers/cookie-helper';
import useNav from 'hooks/nav';
import MainLayout from 'layouts/main-layout/main-layout';

import passengerIcon from 'assets/svgs/icon-passenger-primary.svg';
import checkIcon from 'assets/svgs/icon-checkmark-circle.svg';

import './confirmation.scss';
import TagManager from 'react-gtm-module';

interface ConfirmationPathParams {
  id: string;
}

const DATE_FORMAT = 'D MMM';

export default function Confirmation(): ReactElement {
  const { goTo } = useNav();
  const { locale, t } = useTranslate();
  const params = useParams<ConfirmationPathParams>();
  const { authState, setAuthState } = useAuth();

  const doSignOut = async (evt: any): Promise<void> => {
    evt.preventDefault();
    localStorage.clear();
    eraseCookie('bestcamp-user');
    await Auth.signOut();
    await setAuthState(undefined);
  };

  const reservationDetailsData = useQuery(
    ['reservation', params.id],
    reservationsApi.getReservation,
    {
      enabled: authState,
    },
  );

  const editInformation = (): void => {
    goTo(`/${locale}${PROFILE_PATH}`);
  };

  // Pass purchase event to Google Tag Manager
  // via dataLayer for ecommerce tracking.
  useEffect(() => {
    if (reservationDetailsData.data) {
      const { settledAmount, reservationDays } = reservationDetailsData.data;
      TagManager.dataLayer({
        dataLayer: {
          event: 'purchase',
          category: 'reservation',
          settledAmount: settledAmount,
          quantity: reservationDays.length,
        },
      });
    }
  }, [reservationDetailsData]);

  const parksData = useQuery(['parks', locale], parksApi.getParks);
  let park: Park | null = null;

  if (reservationDetailsData.data && parksData.data) {
    park = parksData.data.filter(
      (park) => park.id === reservationDetailsData.data?.parkId,
    )[0];
  }
  const renderContent = (): ReactElement => {
    if (parksData.isLoading || reservationDetailsData.isLoading) {
      return <PageLoader />;
    }

    return (
      <div className='reservation-confirmation'>
        {reservationDetailsData.data && (
          <div className='user-details'>
            <div className='user-icon'>
              <img src={passengerIcon} alt='Passenger Information' />
            </div>
            <div className='user-info'>
              <h1>{`${reservationDetailsData.data?.customer.firstName} ${reservationDetailsData.data?.customer.lastName}`}</h1>
              <p>{`${reservationDetailsData.data?.customer.email}`}</p>
              <p>{`${reservationDetailsData.data?.customer.phoneNumber}`}</p>
              <div className='user-account-actions'>
                <p>
                  <span onClick={editInformation}>Edit Information</span>
                </p>
                <p>
                  <span onClick={doSignOut}>{t('signout')}</span>
                </p>
              </div>
            </div>
          </div>
        )}
        <hr />
        <div className='reservation-details'>
          <div className='reservation-status'>
            <img src={checkIcon} alt='Status success' />
            <small>Your booking is now confirmed</small>
          </div>
          {reservationDetailsData.data && park && (
            <div className='property-details'>
              {park.images.length > 1 && (
                <div className='property-image'>
                  <img
                    src={`${process.env.REACT_APP_MEDIA_BASE_URL}/${
                      park.images ? park.images[0] : ''
                    }`}
                    alt='Camp Site Thumbnail'
                  />
                </div>
              )}
              {park?.name && <h3>{park.name}</h3>}
              <small className='reservation-date'>
                {moment(reservationDetailsData.data?.startDate).format(
                  DATE_FORMAT,
                )}{' '}
                to{' '}
                {moment(reservationDetailsData.data?.endDate).format(
                  DATE_FORMAT,
                )}
              </small>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <MainLayout
      hostSlug={park?.hostSlug}
      hostUrl={park?.hostUrl}
      logoUrl={park?.logoUrl}
      color={park?.color}
      hideFooter={!!park?.hostUrl}
    >
      {renderContent()}
    </MainLayout>
  );
}
